import type { QuestionnaireResponse } from "@medplum/fhirtypes";

import {
  OTHER_DISPLAY,
  readYesNoOtherDisplayFromAnswer,
  yesNoOtherDisplayToCoding,
} from "@/components/ClearableYesNoRadioField";
import {
  CLIENT_IN_WATER_LINK_ID,
  CLIENT_IN_WATER_OTHER_LINK_ID,
  CLIENT_POSITION_LINK_ID,
  POSITION_DATE_LINK_ID,
  POSITION_DATE_TIME_LINK_ID,
  POSITION_NOTES_LINK_ID,
  POSITION_ROOT_LINK_ID,
  POSITION_SIGNATURE_INITIALS_LINK_ID,
  POSITION_TIME_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/position/constants/position-link-ids";
import type { PositionFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/position/types/position-form";
import { getEmptyPositionFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/position/utils/position-form-defaults";
import {
  parseCodingFromNativeSelect,
  readCodingAnswerAsNativeSelectValue,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import {
  findQuestionnaireResponseItem,
  parseQuestionnaireResponseLocalValueDate,
  readQuestionnaireSignatureInitials,
  readQuestionnaireStringAnswer,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/questionnaire-response-readers";
import { QuiltedHealthCodes } from "@/fhir/enums/quilted-health";
import { Systems } from "@/fhir/enums/systems";

const CLIENT_IN_WATER_OTHER_CODING = {
  system: Systems.QH,
  code: QuiltedHealthCodes.ClientInWaterOther,
  display: OTHER_DISPLAY,
};

export function mapQuestionnaireResponseToPositionFormValues(
  questionnaireResponse: QuestionnaireResponse | undefined,
): PositionFlowsheetFormValues {
  const emptyValues = getEmptyPositionFlowsheetFormValues();
  const root = findQuestionnaireResponseItem(questionnaireResponse?.item, POSITION_ROOT_LINK_ID);
  if (!root?.item) {
    return emptyValues;
  }

  const dateTimeGroup = findQuestionnaireResponseItem(root.item, POSITION_DATE_TIME_LINK_ID);
  const dateAnswer = findQuestionnaireResponseItem(dateTimeGroup?.item, POSITION_DATE_LINK_ID)
    ?.answer?.[0]?.valueDate;
  const timeAnswer = findQuestionnaireResponseItem(dateTimeGroup?.item, POSITION_TIME_LINK_ID)
    ?.answer?.[0]?.valueTime;

  const clientInWaterItem = findQuestionnaireResponseItem(root.item, CLIENT_IN_WATER_LINK_ID);

  return {
    ...emptyValues,
    positionDate: parseQuestionnaireResponseLocalValueDate(dateAnswer),
    positionTime: timeAnswer ?? null,
    clientPosition: readCodingAnswerAsNativeSelectValue(
      findQuestionnaireResponseItem(root.item, CLIENT_POSITION_LINK_ID),
    ),
    clientInWater: readYesNoOtherDisplayFromAnswer(
      clientInWaterItem?.answer?.[0],
      CLIENT_IN_WATER_OTHER_CODING,
    ),
    clientInWaterOther: readQuestionnaireStringAnswer(
      findQuestionnaireResponseItem(root.item, CLIENT_IN_WATER_OTHER_LINK_ID),
    ),
    positionNotes: readQuestionnaireStringAnswer(
      findQuestionnaireResponseItem(root.item, POSITION_NOTES_LINK_ID),
    ),
    signatureInitials: readQuestionnaireSignatureInitials(
      findQuestionnaireResponseItem(root.item, POSITION_SIGNATURE_INITIALS_LINK_ID),
    ),
  };
}

export function mapPositionFormValuesToQuestionnaireValues(
  formValues: PositionFlowsheetFormValues,
): Record<string, unknown> {
  return {
    [POSITION_ROOT_LINK_ID]: {
      [POSITION_DATE_TIME_LINK_ID]: {
        [POSITION_DATE_LINK_ID]: formValues.positionDate,
        [POSITION_TIME_LINK_ID]: formValues.positionTime,
      },
      [CLIENT_POSITION_LINK_ID]: parseCodingFromNativeSelect(formValues.clientPosition),
      [CLIENT_IN_WATER_LINK_ID]: yesNoOtherDisplayToCoding(
        formValues.clientInWater,
        CLIENT_IN_WATER_OTHER_CODING,
      ),
      [CLIENT_IN_WATER_OTHER_LINK_ID]: formValues.clientInWaterOther,
      [POSITION_NOTES_LINK_ID]: formValues.positionNotes,
      [POSITION_SIGNATURE_INITIALS_LINK_ID]: formValues.signatureInitials,
    },
  };
}
